import { InfoIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Flex,
  Heading,
  ListItem,
  Text,
  Tooltip,
  UnorderedList,
  useColorModeValue as mode,
  useColorModeValue,
} from '@chakra-ui/react';
import { ColumnDef } from '@tanstack/react-table';
import ReactTable from 'components/ReactTable';
import StateText from 'components/StateText';
import useSynchronizeHistory from 'queries/useSynchronizeHistory';
import { useTranslation } from 'react-i18next';
import { FaSync } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { SyncDocumentHistory } from 'services/synchronization/synchronization.type';

const Synchronization = () => {
  const { data = [], isFetching } = useSynchronizeHistory();
  const bgColor = useColorModeValue('white', '#363f50');
  const textColor = useColorModeValue('black', 'white');
  const { t } = useTranslation();
  const columns: ColumnDef<SyncDocumentHistory>[] = [
    {
      accessorKey: 'sync_by',
      header: t('synchronization.syncBy'),
    },
    {
      accessorKey: 'spo_connection_display_name',
      header: t('synchronization.connectionName'),
    },
    {
      accessorKey: 'spo_site',
      header: t('synchronization.site'),
    },
    {
      accessorKey: 'state',
      header: t('synchronization.state'),
      cell: (info) => {
        return <StateText statusKey={info.getValue() as string} />;
      },
    },
    {
      accessorKey: 'spo_folder',
      header: t('synchronization.folder'),
      cell: (info) => {
        const isSameFolder =
          info.row.original.sub_folders.length === 1 &&
          info.row.original.sub_folders[0] === info.getValue();
        return (
          <Box alignItems={'center'} gap={4} display={'flex'}>
            <Tooltip
              color={textColor}
              bg={bgColor}
              label={info.getValue() as string}
              placement='top'
            >
              <span>{(info.getValue() as string).split('/').pop() || '/'}</span>
            </Tooltip>
            {!isSameFolder && (
              <Tooltip
                color={textColor}
                bg={bgColor}
                label={
                  <Box p={2}>
                    <Text mb={1} fontSize={14} fontWeight={500}>
                      {info.getValue() as string}
                    </Text>
                    <UnorderedList pl={1}>
                      {info.row.original.sub_folders.map((folder) => (
                        <ListItem key={folder}>
                          {folder.replace(info.getValue() as string, '')}
                        </ListItem>
                      ))}
                    </UnorderedList>
                  </Box>
                }
                placement='left-start'
              >
                <InfoIcon boxSize={3} />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  return (
    <Box px={6}>
      <Flex align={'center'} justify={'space-between'} mb={4}>
        <Heading as='h3' mr='1rem' fontWeight={'bold'} size='md'>
          {t('synchronization.title')}
        </Heading>
        <Link to={'/sync-projects'}>
          <Button
            sx={{
              color: 'gray.500',
              fontWeight: 'bold',
            }}
            leftIcon={<FaSync />}
            variant='amatrium_submit'
          >
            <Text>{t('synchronization.createANewSync')}</Text>
          </Button>
        </Link>
      </Flex>
      <Box border={mode('0.5px solid rgb(209, 213, 219)', '')}>
        <ReactTable columns={columns} data={data} isLoading={isFetching} />
      </Box>
    </Box>
  );
};

export default Synchronization;
